import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../ui/dialog';
import {
  Gem,
  Shield,
  Banknote,
  FileCheck,
  Star,
  Phone,
  Mail,
  CheckCircle2,
  ArrowRight,
  Loader2,
  Clock,
  Percent,
  Building2,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Insurance Quote Dialog
const InsuranceQuoteDialog = ({ providers, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item_type: '',
    item_value: '',
    description: '',
    has_certification: false,
    certification_type: '',
    coverage_type: 'comprehensive',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/jewellery-services/insurance/quote`, {
        ...formData,
        item_value: Number(formData.item_value),
      });
      if (response.data.success) {
        alert(`Quote request submitted! Reference: ${response.data.quote_id}`);
        setOpen(false);
        onSuccess?.();
      }
    } catch (err) {
      alert('Failed to submit quote request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Shield className="h-4 w-4 mr-2" />
          Get Insurance Quote
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Get Jewellery Insurance Quote
          </DialogTitle>
          <DialogDescription>
            Protect your precious jewellery with comprehensive coverage
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Item Type *</Label>
              <Select value={formData.item_type} onValueChange={(v) => setFormData({...formData, item_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ring">Ring</SelectItem>
                  <SelectItem value="necklace">Necklace</SelectItem>
                  <SelectItem value="bracelet">Bracelet</SelectItem>
                  <SelectItem value="earrings">Earrings</SelectItem>
                  <SelectItem value="watch">Watch</SelectItem>
                  <SelectItem value="pendant">Pendant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estimated Value (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 500000"
                value={formData.item_value}
                onChange={(e) => setFormData({...formData, item_value: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Item Description *</Label>
            <Textarea
              placeholder="Describe your jewellery (material, gems, weight, etc.)"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label>Coverage Type</Label>
            <Select value={formData.coverage_type} onValueChange={(v) => setFormData({...formData, coverage_type: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comprehensive">Comprehensive (Theft + Damage + Loss)</SelectItem>
                <SelectItem value="theft_only">Theft Only</SelectItem>
                <SelectItem value="damage_only">Damage Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                placeholder="07X XXX XXXX"
                value={formData.customer_phone}
                onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
              />
            </div>
          </div>

          <Button
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={handleSubmit}
            disabled={loading || !formData.item_type || !formData.item_value || !formData.customer_name || !formData.customer_phone}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
            Submit Quote Request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Loan Application Dialog
const LoanApplicationDialog = ({ providers, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item_type: '',
    item_description: '',
    estimated_value: '',
    loan_amount_requested: '',
    loan_term_months: 12,
    customer_name: '',
    customer_phone: '',
    customer_nic: '',
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/jewellery-services/loans/apply`, {
        ...formData,
        estimated_value: Number(formData.estimated_value),
        loan_amount_requested: Number(formData.loan_amount_requested),
      });
      if (response.data.success) {
        alert(`Loan application submitted! Reference: ${response.data.application_id}`);
        setOpen(false);
        onSuccess?.();
      }
    } catch (err) {
      alert('Failed to submit loan application');
    } finally {
      setLoading(false);
    }
  };

  // Calculate LTV
  const ltv = formData.estimated_value && formData.loan_amount_requested
    ? ((Number(formData.loan_amount_requested) / Number(formData.estimated_value)) * 100).toFixed(1)
    : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-green-600 hover:bg-green-700">
          <Banknote className="h-4 w-4 mr-2" />
          Apply for Gold Loan
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-green-500" />
            Apply for Gold / Jewellery Loan
          </DialogTitle>
          <DialogDescription>
            Get instant cash against your gold and jewellery
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Item Type *</Label>
              <Select value={formData.item_type} onValueChange={(v) => setFormData({...formData, item_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gold_jewellery">Gold Jewellery</SelectItem>
                  <SelectItem value="gold_coins">Gold Coins</SelectItem>
                  <SelectItem value="diamond_jewellery">Diamond Jewellery</SelectItem>
                  <SelectItem value="silver_jewellery">Silver Jewellery</SelectItem>
                  <SelectItem value="watch">Luxury Watch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estimated Value (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 500000"
                value={formData.estimated_value}
                onChange={(e) => setFormData({...formData, estimated_value: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Item Description *</Label>
            <Textarea
              placeholder="Describe your jewellery (weight in grams, karats, etc.)"
              value={formData.item_description}
              onChange={(e) => setFormData({...formData, item_description: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Loan Amount Required (Rs) *</Label>
              <Input
                type="number"
                placeholder="e.g., 300000"
                value={formData.loan_amount_requested}
                onChange={(e) => setFormData({...formData, loan_amount_requested: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Loan Term</Label>
              <Select value={String(formData.loan_term_months)} onValueChange={(v) => setFormData({...formData, loan_term_months: Number(v)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* LTV Indicator */}
          {formData.estimated_value && formData.loan_amount_requested && (
            <div className={`p-3 rounded-lg ${Number(ltv) <= 75 ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm">Loan-to-Value Ratio:</span>
                <Badge className={Number(ltv) <= 75 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {ltv}%
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {Number(ltv) <= 75 ? 'Within acceptable range (max 75%)' : 'May require additional collateral'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>NIC Number *</Label>
              <Input
                placeholder="National ID"
                value={formData.customer_nic}
                onChange={(e) => setFormData({...formData, customer_nic: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Phone Number *</Label>
            <Input
              placeholder="07X XXX XXXX"
              value={formData.customer_phone}
              onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
            />
          </div>

          <Button
            className="w-full bg-green-600 hover:bg-green-700"
            onClick={handleSubmit}
            disabled={loading || !formData.item_type || !formData.estimated_value || !formData.loan_amount_requested || !formData.customer_name || !formData.customer_nic}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
            Submit Loan Application
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const JewelleryFinanceServices = ({ country = 'SL' }) => {
  const [settings, setSettings] = useState(null);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [loanProviders, setLoanProviders] = useState([]);
  const [valuationProviders, setValuationProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [country]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, insuranceRes, loansRes, valuationRes] = await Promise.all([
        axios.get(`${API_URL}/api/jewellery-services/admin/settings/${country}`),
        axios.get(`${API_URL}/api/jewellery-services/insurance/providers?country=${country}`),
        axios.get(`${API_URL}/api/jewellery-services/loans/providers?country=${country}`),
        axios.get(`${API_URL}/api/jewellery-services/valuation/providers?country=${country}`),
      ]);
      setSettings(settingsRes.data);
      setInsuranceProviders(insuranceRes.data.providers || []);
      setLoanProviders(loansRes.data.providers || []);
      setValuationProviders(valuationRes.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch jewellery services:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  const showInsurance = settings?.insurance_enabled !== false && insuranceProviders.length > 0;
  const showLoans = settings?.loans_enabled !== false && loanProviders.length > 0;
  const showValuation = settings?.valuation_enabled !== false && valuationProviders.length > 0;

  if (!showInsurance && !showLoans && !showValuation) {
    return null; // Don't show section if nothing is enabled
  }

  return (
    <div className="space-y-6 mt-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gem className="h-6 w-6 text-yellow-500" />
            Jewellery Financial Services
          </h2>
          <p className="text-gray-500">Insurance, loans, and valuation for your precious items</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Insurance Section */}
        {showInsurance && (
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Shield className="h-5 w-5" />
                Jewellery Insurance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Protect your precious jewellery against theft, loss, and damage
              </p>

              {/* Top Provider */}
              {insuranceProviders[0] && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{insuranceProviders[0].provider_name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs">{insuranceProviders[0].rating}</span>
                      </div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      {insuranceProviders[0].premium_rate}% p.a.
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {insuranceProviders[0].features?.slice(0, 2).map((f, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        <CheckCircle2 className="h-2 w-2 mr-1" />
                        {f}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">
                {insuranceProviders.length} insurance providers available
              </p>

              <InsuranceQuoteDialog providers={insuranceProviders} />
            </CardContent>
          </Card>
        )}

        {/* Loans Section */}
        {showLoans && (
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Banknote className="h-5 w-5" />
                Gold & Jewellery Loans
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Get instant cash against your gold and jewellery
              </p>

              {/* Top Provider */}
              {loanProviders[0] && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{loanProviders[0].provider_name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs">{loanProviders[0].rating}</span>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      {loanProviders[0].interest_rate}% p.a.
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Percent className="h-3 w-3" />
                    <span>Up to {loanProviders[0].max_ltv}% LTV</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">
                {loanProviders.length} loan providers • Rates from {Math.min(...loanProviders.map(p => p.interest_rate))}%
              </p>

              <LoanApplicationDialog providers={loanProviders} />
            </CardContent>
          </Card>
        )}

        {/* Valuation Section */}
        {showValuation && (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <FileCheck className="h-5 w-5" />
                Valuation Services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Get certified valuation for insurance, sale, or loan purposes
              </p>

              {/* Top Provider */}
              {valuationProviders[0] && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{valuationProviders[0].provider_name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs">{valuationProviders[0].rating}</span>
                      </div>
                    </div>
                    {valuationProviders[0].is_featured && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">Featured</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {valuationProviders[0].certifications?.map((c, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">
                {valuationProviders.length} certified valuers available
              </p>

              <Button className="w-full bg-purple-600 hover:bg-purple-700">
                <FileCheck className="h-4 w-4 mr-2" />
                Request Valuation
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JewelleryFinanceServices;
